import { Button } from "components/buttons/button";
import {
  inputClassNames,
  labelClassNames,
  ValidatedInput,
} from "components/forms/input";
import currency from "currency.js";
import { useBasket } from "lib/hooks/basket/use-basket";
import {
  allInputsAreModified,
  inputsAreValid,
  useInput,
} from "lib/hooks/use-input";
import { emailValidator } from "lib/input-validators/email-validator";
import { isPositiveNumberValidator } from "lib/input-validators/is-positive-number";
import { noDecimalsValidator } from "lib/input-validators/no-decimals-validator";
import { numberValidator } from "lib/input-validators/number-validator";
import { useValidators } from "lib/input-validators/use-validators";
import { SubscriptionPageSub } from "pages/shop/subscriptions/[slug]";
import { FormEvent, useState } from "react";

interface Props {
  subscription: SubscriptionPageSub;
}

export const GiftSubscriptionSection = ({ subscription }: Props) => {
  const [_, modifyBasket] = useBasket();
  const [isAdded, setIsAdded] = useState<boolean>(false);

  const credits = useInput<number>(
    1,
    useValidators([
      numberValidator,
      noDecimalsValidator,
      isPositiveNumberValidator,
    ]),
    true
  );
  const message = useInput<string>("");
  const recipient = useInput<string>("", useValidators([emailValidator]), true);

  const saveDisabled =
    !inputsAreValid([credits, recipient]) || !allInputsAreModified([recipient]);

  const handleAddToBasket = (e: FormEvent) => {
    e.preventDefault();
    modifyBasket({
      type: "add",
      payload: {
        type: "subscription",
        id: subscription.id,
        data: {
          price: subscription.price,
          quantity: parseInt(credits.value.toString()),
          subscription: {
            image: subscription.image,
            message: message.value,
            recipient: recipient.value,
            subId: subscription.id,
            subTitle: subscription.title,
          },
        },
      },
    });
    message.init("");
    recipient.init("");
    credits.init(1);
    setIsAdded(true);
  };

  return (
    <>
      <h2 className='header bg-yellow-default px-1 pt-1 w-full'>
        Gift Subscription
      </h2>
      <form className='flex flex-col space-y-2' onSubmit={handleAddToBasket}>
        <ValidatedInput
          showError={recipient.isModified && !recipient.isValid}
          errorMessage={recipient.validation.message}
          bind={recipient.bind}
          label="Recipient's Email"
          type='email'
          id='recipient'
          inputProps={{ placeholder: "jo@example.com" }}
        />
        <label>
          <span className={`${labelClassNames}`}>Message</span>
          <textarea
            placeholder='Leave a message with your gift'
            className={`${inputClassNames}`}
            value={message.value}
            onChange={(e) => message.setValue(e.target.value)}
          />
        </label>
        <ValidatedInput
          showError={!credits.isValid}
          errorMessage={credits.validation.message}
          bind={credits.bind}
          id='credits'
          label={`Months (${currency(subscription.price, {
            fromCents: true,
            symbol: "£",
          }).format()} / month)`}
          type='number'
        />

        <Button
          type='submit'
          buttonType='primary'
          buttonSize='md'
          className='mr-auto'
          onClick={handleAddToBasket}
          disabled={saveDisabled}>
          {isAdded && !recipient.isModified ? (
            <>Added!</>
          ) : (
            <>
              Add to Basket{" "}
              {currency(
                subscription.price * parseInt(credits.value.toString()),
                {
                  fromCents: true,
                  symbol: "£",
                }
              ).format()}
            </>
          )}
        </Button>
      </form>
    </>
  );
};
